import { Heading, Text } from "gatsby-interface"
import * as React from "react"
import { ColorSchemeCss } from "../color-schemes"
import {
  itemTitleCss,
  sectionCss,
  sectionTitleCss,
  heroLedeCss,
  maxSectionWidth,
  iconEyebrowHeadingCss,
  itemCss,
  gap,
} from "../style-utils"
import { AnchorSection } from "./AnchorSection"
import { getSSLPSanitizedHtml } from "../utils"

const containerCss: ColorSchemeCss = theme => ({
  alignItems: `center`,
  display: `flex`,
  flexDirection: `column`,
  position: `relative`,
  margin: `auto`,
  maxWidth: maxSectionWidth.small,
  textAlign: `center`,
  zIndex: 0,
  gridGap: theme.space[10],

  [theme.mediaQueries.tablet]: {
    maxWidth: maxSectionWidth.tablet,
  },
})

const ledeCss: ColorSchemeCss = theme => [
  heroLedeCss(theme),
  {
    [theme.mediaQueries.desktop]: {
      margin: `${theme.space[7]} ${theme.space[15]} ${theme.space[10]} `,
    },
  },
]

const gridCss: ColorSchemeCss = theme => ({
  width: `100%`,
  display: `grid`,
  gridTemplateColumns: `1fr`,
  gridGap: theme.space[gap.desktop],
  color: theme.colorScheme.heading,
  textAlign: `center`,

  [theme.mediaQueries.tablet]: {
    gridTemplateColumns: `1fr 1fr`,
    textAlign: "left",
    gridGap: theme.space[gap.super],
  },
})

const gridItemCss: ColorSchemeCss = theme => ({
  display: `flex`,
  flexDirection: `column`,
  alignItems: `center`,

  [theme.mediaQueries.tablet]: {
    alignItems: `start`,
  },
})

const gridHeadingCss: ColorSchemeCss = theme => [
  itemTitleCss(theme),
  {
    marginBottom: theme.space[3],

    [theme.mediaQueries.desktop]: {
      textAlign: `left`,
    },
  },
]

const iconCss: ColorSchemeCss = theme => ({
  maxHeight: theme.space[13],
  marginBottom: theme.space[5],
})

export interface FeatureGridItem {
  // Heading can contain HTML
  heading: string
  text?: string
  icon?: string | null
}

export interface FeatureGridProps {
  heading: string
  lede?: string
  items: FeatureGridItem[]
}

function FeatureGrid({
  heading,
  lede,
  items = [],
}: FeatureGridProps): JSX.Element {
  return (
    <AnchorSection id={heading} css={sectionCss}>
      <div css={containerCss}>
        <div css={iconEyebrowHeadingCss}>
          {heading ? (
            <Heading
              as="h2"
              css={sectionTitleCss}
              dangerouslySetInnerHTML={{
                __html: getSSLPSanitizedHtml(heading),
              }}
            />
          ) : null}
          {lede ? (
            <Text
              size="XL"
              as="div"
              css={ledeCss}
              dangerouslySetInnerHTML={{
                __html: getSSLPSanitizedHtml(lede),
              }}
            ></Text>
          ) : null}
        </div>
        <div css={gridCss}>
          {items.map((item: FeatureGridItem) => {
            return (
              <div css={gridItemCss} key={item.heading}>
                {item.icon ? (
                  <div css={iconCss}>
                    <img
                      src={item.icon}
                      alt={`${item.heading} icon`}
                      role="presentation"
                      css={theme => ({
                        marginBottom: 0,
                        maxHeight: theme.space[13],
                      })}
                    />
                  </div>
                ) : null}
                {item.heading ? (
                  <Heading
                    as="h3"
                    css={gridHeadingCss}
                    dangerouslySetInnerHTML={{
                      __html: getSSLPSanitizedHtml(item.heading),
                    }}
                  />
                ) : null}
                {item.text ? (
                  <Text
                    as="div"
                    css={itemCss}
                    dangerouslySetInnerHTML={{
                      __html: getSSLPSanitizedHtml(item.text),
                    }}
                  ></Text>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>
    </AnchorSection>
  )
}

export default FeatureGrid
